<script setup lang="ts">
import { ref, computed } from 'vue'
import { useIwiStore } from '@/stores/iwi'
import { usePriorityTrafficStore } from '@/stores/priorityTraffic'
import FosDialog from '@/components/ui/FosDialog.vue'
import type { Iwi, PriorityTrafficIncident } from '@/types'

const iwiStore = useIwiStore()
const ptiStore = usePriorityTrafficStore()

type ResolvedAlert =
  | { type: 'iwi'; data: Iwi; ts: number }
  | { type: 'pti'; data: PriorityTrafficIncident; ts: number }

const resolvedAlerts = computed<ResolvedAlert[]>(() => {
  const iwis = iwiStore.resolvedIwis.map(iwi => ({ type: 'iwi' as const, data: iwi, ts: iwi.resolved_at?.toMillis() ?? 0 }))
  const ptis = ptiStore.resolvedPtis.map(pti => ({ type: 'pti' as const, data: pti, ts: pti.resolved_at?.toMillis() ?? 0 }))
  return [...iwis, ...ptis].sort((a, b) => b.ts - a.ts)
})

// ─── Dialog ───────────────────────────────────────────────────────────────────

const selected = ref<ResolvedAlert | null>(null)
const dialogOpen = ref(false)

function openAlert(alert: ResolvedAlert) {
  selected.value = alert
  dialogOpen.value = true
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatTime(ts: Iwi['resolved_at']) {
  if (!ts) return '—'
  return ts.toDate().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

function formatDateTime(ts: Iwi['declared_at'] | null) {
  if (!ts) return '—'
  return ts.toDate().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

function display(val: unknown): string {
  if (val === null || val === undefined || val === '') return '—'
  if (val === true) return 'Yes'
  if (val === false) return 'No'
  return String(val)
}

const IWI_REPORT_ROWS = [
  ['Chief Complaint', 'chief_complaint'],
  ['Life Threatening', 'severity_life_threatening'],
  ['Patient Location', 'patient_location'],
  ['Point of Contact', 'point_of_contact'],
  ['Transport', 'transport'],
  ['Pickup Location', 'pickup_location'],
  ['Patient Name', 'patient_name'],
  ['Age', 'patient_age'],
  ['Gender', 'patient_sex'],
  ['Patient Agency', 'patient_agency'],
  ['Team Number', 'team_number'],
  ['EMT with Patient', 'emt_with_patient'],
  ['EMT Name', 'emt_name'],
] as const

const IWI_CHECKLIST = [
  { key: 'checklist_team_leader_contacted', label: 'Team Leader contacted Comms with patient info' },
  { key: 'checklist_ops_notified', label: 'Operations Chief (or designee) notified' },
  { key: 'checklist_support_requested', label: 'Ground ambulance / helicopter / other support requested' },
  { key: 'checklist_team_leader_is_poc', label: 'Team Leader confirmed as Point of Contact' },
  { key: 'checklist_air_ground_channel', label: 'Pre-assigned Air-to-Ground channel in use' },
  { key: 'checklist_command_channel_cleared', label: 'Command channel cleared for emergency traffic' },
] as const
</script>

<template>
  <div
    v-if="resolvedAlerts.length > 0"
    class="border border-border rounded bg-surface overflow-hidden"
  >
    <!-- Header -->
    <div class="flex items-center gap-2 px-3 py-2 ">
      <p class="text-[10px] font-label uppercase tracking-widest text-outline">
        Resolved IWIs/Finds
      </p>
      <span class="ml-auto font-mono text-[0.6rem] text-outline">{{ resolvedAlerts.length }}</span>
    </div>

    <!-- List -->
    <div
      class="divide-y divide-border overflow-y-auto"
      :class="resolvedAlerts.length > 3 ? 'max-h-[168px]' : ''"
    >
      <button
        v-for="alert in resolvedAlerts"
        :key="`${alert.type}-${alert.type === 'iwi' ? alert.data.iwi_id : alert.data.pti_id}`"
        type="button"
        class="w-full text-left px-3 py-2 flex flex-col gap-0.5 hover:bg-surface-container-low transition-colors"
        @click="openAlert(alert)"
      >
        <div class="flex items-center justify-between gap-2">
          <div class="flex items-center gap-1.5 min-w-0">
            <span
              class="font-headline font-black text-[0.55rem] uppercase tracking-wider px-1 py-0.5 rounded border shrink-0"
              :class="alert.type === 'iwi'
                ? 'text-red-600 dark:text-red-400 border-red-500/40 bg-red-500/10'
                : 'text-amber-600 dark:text-amber-400 border-amber-500/40 bg-amber-500/10'"
            >
              {{ alert.type === 'iwi' ? 'IWI' : 'Find' }}
            </span>
            <span class="font-mono text-xs text-on-surface truncate">Team {{ alert.type === 'iwi' ? alert.data.declared_by_team_identifier : alert.data.declared_by_team_identifier }}</span>
          </div>
          <span class="font-mono text-[0.65rem] text-outline shrink-0 tabular-nums">{{ formatTime(alert.data.resolved_at) }}</span>
        </div>
        <div class="flex items-center justify-between gap-2 pl-0.5">
          <span class="font-mono text-[0.6rem] text-outline truncate">{{ alert.data.resolved_by_name ?? '—' }}</span>
          <span
            v-if="alert.type === 'iwi' ? alert.data.chief_complaint : (alert.data.location_address || alert.data.location_coordinates)"
            class="font-mono text-[0.6rem] text-on-surface-variant truncate text-right"
          >
            {{ alert.type === 'iwi' ? alert.data.chief_complaint : (alert.data.location_address || alert.data.location_coordinates) }}
          </span>
        </div>
      </button>
    </div>
  </div>

  <!-- Detail dialog -->
  <FosDialog
    :open="dialogOpen"
    :title="selected?.type === 'iwi' ? 'IWI — Incident Within Incident' : 'Priority Traffic'"
    max-width="max-w-2xl"
    @update:open="dialogOpen = $event"
  >
    <template v-if="selected">
      <!-- Shared resolved banner -->
      <div class="mb-5 flex items-center gap-2.5 px-3 py-2 bg-green-600/10 border border-green-600/30 rounded">
        <span class="material-symbols-outlined text-green-600 dark:text-green-400 text-sm leading-none">check_circle</span>
        <div class="font-mono text-xs text-green-700 dark:text-green-400">
          Resolved at <strong>{{ formatTime(selected.data.resolved_at) }}</strong> by <strong>{{ selected.data.resolved_by_name ?? '—' }}</strong>
        </div>
      </div>

      <!-- IWI detail -->
      <template v-if="selected.type === 'iwi'">
        <p class="font-mono text-xs text-outline mb-1">
          Team <strong class="text-on-surface">{{ selected.data.declared_by_team_identifier }}</strong>
          declared at {{ formatDateTime(selected.data.declared_at) }}
        </p>

        <div class="mt-4 grid grid-cols-2 gap-6">
          <!-- Report fields -->
          <div>
            <p class="font-headline font-black text-[0.65rem] uppercase tracking-widest text-outline mb-3">Incident Report</p>
            <dl class="grid grid-cols-2 gap-x-4 gap-y-1.5 font-mono text-xs">
              <template v-for="[label, field] in IWI_REPORT_ROWS" :key="field">
                <dt class="text-outline uppercase tracking-wider text-[0.6rem] self-center">{{ label }}</dt>
                <dd class="text-on-surface-variant">{{ display((selected.data as Record<string, unknown>)[field]) }}</dd>
              </template>
            </dl>
          </div>

          <!-- Checklist -->
          <div>
            <p class="font-headline font-black text-[0.65rem] uppercase tracking-widest text-outline mb-3">Response Checklist</p>
            <ul class="flex flex-col gap-2">
              <li
                v-for="step in IWI_CHECKLIST"
                :key="step.key"
                class="flex items-start gap-2"
              >
                <span
                  class="mt-0.5 w-4 h-4 shrink-0 border rounded flex items-center justify-center"
                  :class="(selected.data as Record<string, unknown>)[step.key]
                    ? 'bg-status-on-scene border-status-on-scene text-white'
                    : 'bg-surface-container-high border-border'"
                >
                  <span
                    v-if="(selected.data as Record<string, unknown>)[step.key]"
                    class="material-symbols-outlined text-[12px] leading-none"
                  >check</span>
                </span>
                <span
                  class="font-mono text-xs leading-snug"
                  :class="(selected.data as Record<string, unknown>)[step.key] ? 'text-outline line-through' : 'text-on-surface-variant'"
                >
                  {{ step.label }}
                </span>
              </li>
            </ul>
          </div>
        </div>
      </template>

      <!-- PTI detail -->
      <template v-else-if="selected.type === 'pti'">
        <p class="font-mono text-xs text-outline mb-4">
          Team <strong class="text-on-surface">{{ selected.data.declared_by_team_identifier }}</strong>
          declared at {{ formatDateTime(selected.data.declared_at) }}
        </p>

        <div class="flex flex-col gap-4">
          <!-- Location -->
          <div>
            <p class="font-headline font-black text-[0.65rem] uppercase tracking-widest text-outline mb-2">Location</p>
            <dl class="grid grid-cols-2 gap-x-4 gap-y-1.5 font-mono text-xs">
              <dt class="text-outline uppercase tracking-wider text-[0.6rem]">Address / Assignment</dt>
              <dd class="text-on-surface-variant">{{ display(selected.data.location_address) }}</dd>
              <dt class="text-outline uppercase tracking-wider text-[0.6rem]">Coordinates</dt>
              <dd class="text-on-surface-variant">{{ display(selected.data.location_coordinates) }}</dd>
            </dl>
          </div>

          <!-- Resources -->
          <div>
            <p class="font-headline font-black text-[0.65rem] uppercase tracking-widest text-outline mb-2">Resources Requested</p>
            <div v-if="selected.data.resources_requested.length > 0" class="flex flex-wrap gap-1.5">
              <span
                v-for="resource in selected.data.resources_requested"
                :key="resource"
                class="inline-flex items-center px-2 py-0.5 bg-amber-500/20 text-amber-700 dark:text-amber-400 font-mono text-xs rounded border border-amber-500/30"
              >
                {{ resource }}
              </span>
            </div>
            <p v-else class="font-mono text-xs text-outline italic">None recorded</p>
          </div>

          <!-- Notes -->
          <div v-if="selected.data.notes">
            <p class="font-headline font-black text-[0.65rem] uppercase tracking-widest text-outline mb-2">Notes</p>
            <p class="font-mono text-sm text-on-surface">{{ selected.data.notes }}</p>
          </div>
        </div>
      </template>
    </template>
  </FosDialog>
</template>
